import {
  Box,
  Button,
  CircularProgress,
  Stack,
  Typography,
  useTheme
} from '@rabex-kit/rabex-ui';
import React, { FC, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import QRCode from 'react-qr-code';
import {
  useGenerateQrCodeMutation,
  useLoginWithQrCodeMutation
} from '../../services/auth';
import { returnPathFrom } from '@/utils/loginReturn';
import useFinishSignIn from '@/hooks/useFinishSignIn';
import RabexLogo from '../../static/images/svg/RabexInsideQr.svg';

const StatusMap = {
  QR_LOGIN_UNSPECIFIED: false,
  QR_LOGIN_NEW: false,
  QR_LOGIN_EXPIRED: false,
  QR_LOGIN_VERIFIED: true
};

const LoginWithQrCode: FC = () => {
  const finishSignIn = useFinishSignIn();
  const location = useLocation();
  const returnTo = returnPathFrom(location.state);
  const { palette } = useTheme();
  // Per mounted panel: module-level timers were shared by every instance.
  const regenerateTimer = useRef<ReturnType<typeof setInterval>>();
  const verifiedTimer = useRef<ReturnType<typeof setInterval>>();
  const [times, setTimes] = useState<{
    reGenerate: null | number;
    verified: null | number;
  }>({ reGenerate: null, verified: null });
  const [generateQrCode, { data, isLoading, isError }] =
    useGenerateQrCodeMutation();
  const qrCode = data?.data.qrCode;
  // The poll interval outlives renders, so it reads the code through a ref instead of the one
  // captured when the interval was created.
  const qrCodeRef = useRef(qrCode);
  qrCodeRef.current = qrCode;
  const [login] = useLoginWithQrCodeMutation();

  const generateNewQrCode = () => {
    generateQrCode()
      .unwrap()
      .then((res) => {
        const verified = +(Number(res.data.countDownSeconds) / 6).toFixed(0); // seconds;
        const reGenerate = +(
          Number(res.data.countDownSeconds) -
          (verified - 1)
        ).toFixed(0); // seconds
        setTimes({ verified, reGenerate });
      })
      .catch(() => {
        // network blip — the regenerate timer tries again
      });
  };
  const tryForLogin = () => {
    const currentCode = qrCodeRef.current;
    if (!currentCode) return;
    login({ qrCode: currentCode })
      .unwrap()
      .then((res) => {
        const isVerified = StatusMap[res.data.status];
        if (isVerified) {
          finishSignIn(res.data, 'login', returnTo);
        } else if (
          res.data.status === 'QR_LOGIN_EXPIRED' &&
          currentCode === qrCodeRef.current
        ) {
          // Stop polling the dead code; a fresh one restarts the timers.
          clearInterval(verifiedTimer.current);
          generateNewQrCode();
        }
      })
      .catch(() => {
        // network blip — the next poll tries again
      });
  };

  // A fresh code every time the panel mounts. A module-level "already generated" flag used to
  // stop this after the first mount, so returning to login (or to this tab) showed a dead code.
  useEffect(() => {
    generateNewQrCode();
  }, []);

  // Nobody scans a hidden tab, so polling and regenerating pause; coming back, the old code has
  // likely expired, so a fresh one is generated straight away.
  const [isTabVisible, setIsTabVisible] = useState(
    () => document.visibilityState === 'visible'
  );
  const wasHidden = useRef(false);
  useEffect(() => {
    const onVisibilityChange = () =>
      setIsTabVisible(document.visibilityState === 'visible');
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () =>
      document.removeEventListener('visibilitychange', onVisibilityChange);
  }, []);
  useEffect(() => {
    if (!isTabVisible) {
      wasHidden.current = true;
    } else if (wasHidden.current) {
      wasHidden.current = false;
      generateNewQrCode();
    }
  }, [isTabVisible]);

  useEffect(() => {
    if (times.reGenerate && isTabVisible) {
      regenerateTimer.current = setInterval(() => {
        generateNewQrCode();
      }, times.reGenerate * 1000);
    }
    return () => {
      clearInterval(regenerateTimer.current);
    };
  }, [times, isTabVisible]);

  useEffect(() => {
    if (times.verified && isTabVisible) {
      verifiedTimer.current = setInterval(() => {
        tryForLogin();
      }, times.verified * 1000);
    }
    return () => {
      clearInterval(verifiedTimer.current);
    };
  }, [times, isTabVisible]);

  return (
    <Stack
      gap={1}
      alignItems="center"
      sx={{
        zIndex: 2,
        width: '100%',
        position: 'absolute',
        transform: 'translate(-50%, -33%)',
        top: '50%',
        left: '50%'
      }}>
      <Stack
        sx={{ position: 'relative' }}
        alignItems="center"
        justifyContent="center">
        {/* No code yet (loading, or the last generate failed): a spinner or a retry over a faded
            placeholder, never a scannable code that means nothing. */}
        {!qrCode && (
          <Stack
            sx={{ position: 'absolute', zIndex: 3 }}
            alignItems="center"
            gap={1}>
            {isError && !isLoading ? (
              <Button
                variant="containedSecondary"
                size="sm"
                onClick={generateNewQrCode}>
                تلاش مجدد
              </Button>
            ) : (
              <CircularProgress size={25} />
            )}
          </Stack>
        )}
        <Box
          width={150}
          height={150}
          sx={{
            opacity: qrCode ? 1 : 0.1,
            filter: qrCode ? 'none' : 'blur(3px)',
            zIndex: 1,
            transition: 'opacity 0.3s'
          }}>
          <QRCode size={150} value={qrCode || ' '} viewBox="0 0 150 150" />
        </Box>
        <img
          src={RabexLogo}
          alt="rabex-logo"
          draggable={false}
          style={{
            zIndex: 2,
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            userSelect: 'none', // Prevent selection
            pointerEvents: 'none' // Disable pointer events (like clicking, dragging)
          }}
        />
      </Stack>
      <Typography variant="body1" weight="semiBold" mt={6}>
        دانلود اپلیکیشن
      </Typography>
      <Typography
        variant="body2"
        color={
          palette.mode === 'light'
            ? palette.textColor[900]
            : palette.textColor[200]
        }>
        کد را با تلفن همراه اسکن کنید.
      </Typography>
    </Stack>
  );
};

export default LoginWithQrCode;
